import type { AccountContext } from '@/types'

/**
 * Apply the account's liff_theme as CSS custom properties. Extracted from the
 * two hand-copied blocks in main.tsx (the dev-fallback copy had drifted and
 * skipped the LIFF-M1 sanitisation — this is the sanitised version for every
 * caller).
 *
 * LIFF-M1: theme values are operator-controlled but interpolated into CSS
 * contexts — sanitise so a value can't break out of url()/quotes.
 */
export function applyAccountTheme(account: AccountContext): void {
  const theme = account.liff_theme
  if (!theme) return

  const safeUrl = (u?: string) => (u && /^https?:\/\/[^"'()\s]+$/.test(u) ? u : null)
  const safeFont = (f?: string) => (f && /^[A-Za-z0-9 ]+$/.test(f) ? f : null)

  const root = document.documentElement
  if (theme.primary_color) root.style.setProperty('--liff-primary', theme.primary_color)
  if (theme.button_color) root.style.setProperty('--liff-button', theme.button_color)
  if (theme.login_bg_color) root.style.setProperty('--liff-login-bg', theme.login_bg_color)
  const loginBg = safeUrl(theme.login_bg_image)
  if (loginBg) root.style.setProperty('--liff-login-bg-image', `url(${loginBg})`)
  if (theme.home_hero_color) root.style.setProperty('--liff-hero-color', theme.home_hero_color)
  const heroBg = safeUrl(theme.home_hero_image)
  if (heroBg) root.style.setProperty('--liff-hero-image', `url(${heroBg})`)
  const font = safeFont(theme.font_family)
  if (font) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@400;500;600;700&display=swap`
    document.head.appendChild(link)
    root.style.setProperty('--liff-font', `'${font}', system-ui, sans-serif`)
  }
}
